import { useEffect, useRef, useState } from 'react';

/**
 * Плавный счётчик `0 → target` на requestAnimationFrame (easeOut).
 *
 * Возвращает текущее (дробное) значение — округляйте при выводе. При смене
 * `target` анимация продолжается от предыдущего значения. Уважает
 * `prefers-reduced-motion`: при включённом — сразу выставляет `target` без
 * анимации (глобальный CSS-гард JS-анимации не покрывает, поэтому гард здесь).
 *
 * @param target  целевое число
 * @param durationMs длительность анимации (по умолчанию 800мс)
 */
export function useCountUp(target: number, durationMs = 800): number {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduce || !Number.isFinite(target)) {
      fromRef.current = Number.isFinite(target) ? target : 0;
      setValue(fromRef.current);
      return;
    }

    const from = fromRef.current;
    const delta = target - from;
    if (delta === 0) {
      setValue(target);
      return;
    }

    let start: number | null = null;
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      if (start === null) start = now;
      const p = Math.min(1, (now - start) / durationMs);
      setValue(from + delta * easeOut(p));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}
